import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  CheckCircle2, XCircle, Loader2, Upload, AlertCircle, Trash2, Undo2,
  FileImage, FileUp, Info, Search,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  listPixProofs, listAwaitingPixOrders, uploadPixProof, decidePixProof,
  deletePixProof, revertPixProof, setBankReconciled, reconcileWithBankCsv,
} from "@/lib/api/pix-proofs.functions";

import { BlockedPayersDialog } from "@/components/finance/BlockedPayersDialog";
export const Route = createFileRoute("/_authenticated/m/confirmacao-pagamentos")({
  component: Page,
});

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n || 0);

const fmtDateTime = (iso: string) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "";

type Proof = {
  id: string;
  image_signed_url: string | null;
  image_url: string | null;
  source: string;
  origin_phone: string | null;
  payer_name: string | null;
  payer_cpf_middle: string | null;
  amount: number | null;
  pix_date: string | null;
  status: "aguardando" | "confirmado" | "rejeitado";
  match_level: "diamond" | "gold" | "silver" | "simples" | "none";
  linked_kind: "uniform" | "product" | "charge" | null;
  linked_id: string | null;
  linked_student_name: string | null;
  linked_label: string | null;

  resend_count?: number | null;
  last_resend_at?: string | null;
  bank_reconciled: boolean;
  bank_reconciled_at: string | null;
  reviewed_at: string | null;
  created_at: string;
};

type AwaitingOrder = {
  kind: "uniform" | "product" | "charge";
  id: string;
  amount: number;
  item_name: string;
  student_name: string;
  parent_name: string;
  parent_phone: string;
  parent_cpf: string;
  created_at: string;
};

const onlyDigits = (s: string | null | undefined) => (s || "").replace(/\D/g, "");
const normName = (s: string | null | undefined) =>
  (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

function scoreCandidate(proof: Proof, c: AwaitingOrder): Proof["match_level"] {
  if (proof.amount == null || Math.abs(c.amount - proof.amount) >= 0.01) return "none";
  const mid = onlyDigits(c.parent_cpf).slice(3, 9);
  if (proof.payer_cpf_middle && mid && proof.payer_cpf_middle === mid) return "diamond";
  if (c.parent_name && proof.payer_name && normName(c.parent_name) === normName(proof.payer_name)) return "gold";
  const pp = onlyDigits(c.parent_phone);
  const op = onlyDigits(proof.origin_phone);
  if (pp && op && (pp.endsWith(op.slice(-10)) || op.endsWith(pp.slice(-10)))) return "silver";
  return "simples";
}

function maskCpfMiddle(cpf: string) {
  const d = onlyDigits(cpf);
  if (d.length !== 11) return "";
  return `***.${d.slice(3,6)}.${d.slice(6,9)}.**`;
}


function MatchBadge({ level }: { level: Proof["match_level"] }) {
  if (level === "diamond") return <Badge className="bg-cyan-600 hover:bg-cyan-600">💎 Match Diamond (CPF)</Badge>;
  if (level === "gold")    return <Badge className="bg-amber-500 hover:bg-amber-500">🥇 Match Gold (Nome)</Badge>;
  if (level === "silver")  return <Badge variant="secondary">🥈 Match Silver (Telefone)</Badge>;
  if (level === "simples") return <Badge variant="outline">Match Simples (Valor)</Badge>;
  return <Badge variant="outline">Sem match</Badge>;
}

function Page() {
  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border p-6 flex items-start gap-4">
        <div className="rounded-xl bg-primary/15 p-3 text-primary"><CheckCircle2 className="h-7 w-7" /></div>
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Confirmação de Pagamentos</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Confira e valide os comprovantes processados pela nossa IA.
          </p>
        </div>
        <BlockedPayersDialog />
      </div>
      <ProofsTabs />
    </div>
  );
}

function ProofsTabs() {
  const listFn = useServerFn(listPixProofs);
  const [tab, setTab] = useState<"aguardando" | "confirmado" | "rejeitado">("aguardando");
  const [loading, setLoading] = useState(true);
  const [proofs, setProofs] = useState<Proof[]>([]);

  // Filters
  const [search, setSearch] = useState("");
  const [month, setMonth] = useState<string>(""); // YYYY-MM
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await listFn({ data: {
        status: tab,
        search: search.trim() || null,
        month: month || null,
        date_from: month ? null : (dateFrom || null),
        date_to: month ? null : (dateTo || null),
      }});
      setProofs((r.items ?? []) as Proof[]);
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, month, dateFrom, dateTo]);

  const [reconFilter, setReconFilter] = useState<"todos" | "conferidos" | "nao">("nao");
  const reconFiltered = useMemo(() => {
    if (reconFilter === "conferidos") return proofs.filter((p) => p.bank_reconciled);
    if (reconFilter === "nao") return proofs.filter((p) => !p.bank_reconciled);
    return proofs;
  }, [proofs, reconFilter]);

  const counts = useMemo(() => ({
    aguardando: tab === "aguardando" ? proofs.length : null,
    confirmado: tab === "confirmado" ? proofs.length : null,
    rejeitado: tab === "rejeitado" ? proofs.length : null,
  }), [proofs, tab]);

  const clearFilters = () => { setSearch(""); setMonth(""); setDateFrom(""); setDateTo(""); setTimeout(load, 0); };


  return (
    <Tabs value={tab} onValueChange={(v: any) => setTab(v)}>
      <TabsList>
        <TabsTrigger value="aguardando">Aguardando Validação{counts.aguardando != null && <Badge variant="secondary" className="ml-2">{counts.aguardando}</Badge>}</TabsTrigger>
        <TabsTrigger value="confirmado">Confirmados{counts.confirmado != null && <Badge variant="secondary" className="ml-2">{counts.confirmado}</Badge>}</TabsTrigger>
        <TabsTrigger value="rejeitado">Descartados{counts.rejeitado != null && <Badge variant="secondary" className="ml-2">{counts.rejeitado}</Badge>}</TabsTrigger>
      </TabsList>

      <Card className="mt-3">
        <CardContent className="p-3 grid grid-cols-1 sm:grid-cols-[1fr_160px_160px_160px_auto] gap-2 items-end">
          <div>
            <Label className="text-xs">Buscar por nome do aluno/pagador ou valor</Label>
            <form onSubmit={(e) => { e.preventDefault(); load(); }}>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ex.: Miguel  ou  150,00" />
            </form>
          </div>
          <div>
            <Label className="text-xs">Mês</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Data inicial</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} disabled={!!month} />
          </div>
          <div>
            <Label className="text-xs">Data final</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} disabled={!!month} />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => load()}>Filtrar</Button>
            <Button variant="ghost" onClick={clearFilters}>Limpar</Button>
          </div>
        </CardContent>
      </Card>

      <TabsContent value="aguardando" className="space-y-4">
        <InfoBanner />
        <ProofList loading={loading} proofs={proofs} mode="aguardando" reload={load} />
      </TabsContent>

      <TabsContent value="confirmado" className="space-y-4">
        <ReconciliationPanel proofs={proofs} reload={load} filter={reconFilter} setFilter={setReconFilter} />
        <ProofList loading={loading} proofs={reconFiltered} mode="confirmado" reload={load} />
      </TabsContent>


      <TabsContent value="rejeitado">
        <ProofList loading={loading} proofs={proofs} mode="rejeitado" reload={load} />
      </TabsContent>
    </Tabs>
  );
}

function InfoBanner() {
  return (
    <div className="rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm flex gap-2">
      <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
      <div>
        A IA atua de forma proativa avaliando o <b>Valor, CPF, Nome e Telefone</b>. Recebimentos com
        <b> Match Diamond</b> (CPF) ou <b>Match Gold</b> (Nome) <b>são validados automaticamente</b>.
        Quando há correspondência de <b>Match Silver</b> (Telefone) ou <b>Match Simples</b> (Valor),
        o comprovante fica aqui sugerido para <b>Conciliação Manual</b>.
      </div>
    </div>
  );
}

function UploadProofBox({ onUploaded }: { onUploaded: () => void }) {
  const uploadFn = useServerFn(uploadPixProof);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");

  const handleFile = async (file: File) => {
    setBusy(true);
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      let bin = ""; for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i]);
      const b64 = btoa(bin);
      const r: any = await uploadFn({ data: {
        image_base64: b64, mime: file.type || "image/jpeg",
        origin_phone: phone || null, source: "manual",
      }});
      if (r.auto_confirmed) toast.success(`Comprovante validado automaticamente (${r.match_level}).`);
      else if (r.match_level !== "none") toast.info(`Sugestão de match: ${r.match_level}. Confirme abaixo.`);
      else toast.info("Comprovante carregado. Vincule manualmente abaixo.");
      setPhone("");
      onUploaded();
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <Card>
      <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-[1fr_220px_auto] gap-3 items-end">
        <div>
          <Label className="text-xs">Telefone de origem (opcional — para match Silver)</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(00) 00000-0000" />
        </div>
        <div>
          <Label className="text-xs">&nbsp;</Label>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => {
            const f = e.target.files?.[0]; if (f) handleFile(f);
          }} />
          <Button className="w-full" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileUp className="h-4 w-4 mr-2" />}
            Enviar comprovante (PIX)
          </Button>
        </div>
        <div className="text-xs text-muted-foreground flex gap-1 items-center"><Info className="h-3 w-3" /> IA lê valor, CPF, nome e data.</div>
      </CardContent>
    </Card>
  );
}

function ProofList({ loading, proofs, mode, reload }: {
  loading: boolean; proofs: Proof[]; mode: Proof["status"]; reload: () => void;
}) {
  const delFn = useServerFn(deletePixProof);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => { setSelected(new Set()); }, [mode, proofs]);

  const toggle = (id: string) => {
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const allChecked = proofs.length > 0 && selected.size === proofs.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(proofs.map((p) => p.id)));

  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!confirm(`Excluir ${selected.size} comprovante(s) selecionado(s)?`)) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of Array.from(selected)) {
      try { await delFn({ data: { id } }); ok++; } catch { fail++; }
    }
    setBulkBusy(false);
    if (ok) toast.success(`${ok} excluído(s).`);
    if (fail) toast.error(`${fail} não puderam ser excluídos.`);
    reload();
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  if (!proofs.length) return <div className="text-center py-12 text-muted-foreground">Nenhum comprovante {mode}.</div>;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={allChecked} onChange={toggleAll} className="h-4 w-4" />
          Selecionar todos ({proofs.length})
        </label>
        <div className="flex items-center gap-2">
          {selected.size > 0 && <span className="text-xs text-muted-foreground">{selected.size} selecionado(s)</span>}
          <Button variant="destructive" size="sm" disabled={!selected.size || bulkBusy} onClick={bulkDelete}>
            {bulkBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
            Excluir selecionados
          </Button>
        </div>
      </div>
      {proofs.map((p) => (
        <ProofCard key={p.id} proof={p} mode={mode} reload={reload}
          checked={selected.has(p.id)} onToggle={() => toggle(p.id)} />
      ))}
    </div>
  );
}

function ProofCard({ proof, mode, reload, checked, onToggle }: { proof: Proof; mode: Proof["status"]; reload: () => void; checked?: boolean; onToggle?: () => void; }) {
  const decideFn = useServerFn(decidePixProof);
  const delFn = useServerFn(deletePixProof);
  const revertFn = useServerFn(revertPixProof);
  const setReconFn = useServerFn(setBankReconciled);
  const awaitingFn = useServerFn(listAwaitingPixOrders);

  const [awaiting, setAwaiting] = useState<AwaitingOrder[]>([]);
  const [linkVal, setLinkVal] = useState<string>("");
  const [acting, setActing] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [qAmount, setQAmount] = useState("");
  const [qItem, setQItem] = useState("");
  const [qCpf, setQCpf] = useState("");
  const [qName, setQName] = useState("");

  const selectedOrder = useMemo(
    () => awaiting.find((o) => `${o.kind}:${o.id}` === linkVal) ?? null,
    [awaiting, linkVal],
  );

  const normTxt = (v: string) =>
    (v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const searchResults = useMemo(() => {
    const rank = { diamond: 0, gold: 1, silver: 2, simples: 3, none: 4 } as const;
    return awaiting
      .filter((o) => {
        if (qAmount && Math.abs(o.amount - Number(qAmount.replace(",", "."))) > 0.01) return false;
        if (qItem && !normTxt(o.item_name ?? "").includes(normTxt(qItem))) return false;
        if (qCpf && !(o.parent_cpf ?? "").replace(/\D/g, "").includes(qCpf.replace(/\D/g, ""))) return false;
        if (qName) {
          const hay = normTxt(`${o.student_name ?? ""} ${o.parent_name ?? ""}`);
          if (!hay.includes(normTxt(qName))) return false;
        }
        return true;
      })
      .map((o) => ({ o, level: scoreCandidate(proof, o) }))
      .sort((a, b) => rank[a.level] - rank[b.level]);
  }, [awaiting, qAmount, qItem, qCpf, qName, proof]);

  useEffect(() => {
    if (mode !== "aguardando") return;
    awaitingFn().then((r: any) => setAwaiting(r.items ?? []));
  }, [mode, awaitingFn]);

  // Pre-fill best suggestion: order with exact value
  useEffect(() => {
    if (mode !== "aguardando" || !proof.amount) return;
    const hit = awaiting.find((o) => Math.abs(o.amount - (proof.amount ?? 0)) < 0.01);
    if (hit && !linkVal) setLinkVal(`${hit.kind}:${hit.id}`);
  }, [awaiting, proof.amount, mode, linkVal]);

  const doConfirm = async () => {
    if (!linkVal) { toast.error("Selecione a cobrança."); return; }
    const [kind, id] = linkVal.split(":") as ["uniform" | "product" | "charge", string];
    setActing(true);
    try {
      await decideFn({ data: { id: proof.id, decision: "confirm", link_kind: kind, link_id: id } });
      toast.success("Confirmado — entrou no Caixa.");
      reload();
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
    finally { setActing(false); }
  };
  const reject = async () => {
    if (!confirm("Rejeitar este comprovante?")) return;
    setActing(true);
    try { await decideFn({ data: { id: proof.id, decision: "reject" } }); toast.success("Rejeitado."); reload(); }
    catch (e: any) { toast.error(e?.message ?? "Erro"); }
    finally { setActing(false); }
  };
  const remove = async () => {
    if (!confirm("Excluir comprovante?")) return;
    try { await delFn({ data: { id: proof.id } }); toast.success("Excluído."); reload(); }
    catch (e: any) { toast.error(e?.message ?? "Erro"); }
  };
  const revert = async () => {
    if (!confirm("Reverter para 'Aguardando'? A entrada no caixa será removida.")) return;
    try { await revertFn({ data: { id: proof.id } }); toast.success("Revertido."); reload(); }
    catch (e: any) { toast.error(e?.message ?? "Erro"); }
  };

  const exactHit = mode === "aguardando" && awaiting.some((o) => Math.abs(o.amount - (proof.amount ?? 0)) < 0.01);

  return (
    <Card>
      <CardContent className="p-4 grid grid-cols-1 md:grid-cols-[24px_180px_1fr] gap-4">
        <div className="flex items-start justify-center pt-1">
          {onToggle && (
            <input type="checkbox" className="h-4 w-4" checked={!!checked} onChange={onToggle} aria-label="Selecionar comprovante" />
          )}
        </div>
        <div className="rounded-lg border bg-muted/30 flex items-center justify-center min-h-[160px] overflow-hidden">

          {proof.image_signed_url ? (
            /\.pdf(\?|$)/i.test(proof.image_url ?? "") ? (
              <a href={proof.image_signed_url} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 p-4 text-xs text-primary hover:underline">
                <FileImage className="h-10 w-10" />
                Abrir PDF do comprovante
              </a>
            ) : (
              <a href={proof.image_signed_url} target="_blank" rel="noreferrer">
                <img
                  src={proof.image_signed_url}
                  alt="Comprovante"
                  className="max-h-[200px] object-contain"
                  onError={(e) => {
                    // Comprovantes antigos foram salvos como .jpg mesmo sendo PDF.
                    // Trocar o <img> quebrado por um link "Abrir arquivo".
                    const a = document.createElement("a");
                    a.href = proof.image_signed_url!;
                    a.target = "_blank";
                    a.rel = "noreferrer";
                    a.className = "flex flex-col items-center gap-2 p-4 text-xs text-primary hover:underline";
                    a.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg><span>Abrir comprovante</span>';
                    e.currentTarget.replaceWith(a);
                  }}
                />
              </a>
            )
          ) : <FileImage className="h-10 w-10 text-muted-foreground/40" />}
        </div>

        <div className="space-y-3">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="flex flex-wrap items-center gap-2">
              {mode === "aguardando" && <Badge className="bg-amber-500 hover:bg-amber-500">Aguardando Validação</Badge>}
              {mode === "confirmado" && <Badge className="bg-emerald-600 hover:bg-emerald-600">Confirmado</Badge>}
              {mode === "rejeitado" && <Badge variant="destructive">Rejeitado</Badge>}
              <MatchBadge level={proof.match_level} />
              {!proof.payer_cpf_middle && (
                <Badge variant="outline" className="border-amber-500 text-amber-600">CPF/CNPJ não identificado — conferir manualmente</Badge>
              )}
              {(proof.resend_count ?? 0) > 0 && (
                <Badge variant="outline" className="border-sky-500 text-sky-600">
                  Reenviado {(proof.resend_count ?? 0) + 1}x{proof.last_resend_at ? ` · ${fmtDateTime(proof.last_resend_at)}` : ""}
                </Badge>
              )}
              {mode === "confirmado" && (
                proof.bank_reconciled
                  ? <Badge variant="outline" className="border-emerald-500 text-emerald-600">Conferido no Extrato</Badge>
                  : <Badge variant="outline" className="border-red-400 text-red-500">Não conferido no extrato</Badge>
              )}

            </div>
            <div className="text-xs text-muted-foreground text-right">
              <div className="text-[11px] uppercase tracking-wide">Captura do comprovante</div>
              <div className="font-medium text-foreground">{fmtDateTime(proof.created_at)}</div>
            </div>
          </div>

          {mode === "confirmado" && (proof.linked_student_name || proof.linked_label) && (
            <div className="rounded-lg border border-emerald-300/60 bg-emerald-50 dark:bg-emerald-950/30 p-3 text-sm flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
              <div className="flex flex-wrap items-center gap-x-2">
                <span className="text-muted-foreground">Match aplicado a:</span>
                <span className="font-semibold">{proof.linked_student_name || "—"}</span>
                {proof.linked_label && (
                  <>
                    <span className="text-muted-foreground">·</span>
                    <span>{proof.linked_label}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {mode === "aguardando" && !exactHit && (
            <div className="rounded-md border border-dashed bg-muted/30 p-2 text-xs text-muted-foreground">
              Nenhuma cobrança pendente encontrada com este valor exato.
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm rounded-lg border p-3 bg-card">
            <div>
              <div className="text-[11px] text-muted-foreground">Valor do PIX (IA)</div>
              <div className="font-bold text-primary">{proof.amount != null ? fmtMoney(proof.amount) : "—"}</div>
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground">Pagador</div>
              <div className="font-medium">{proof.payer_name || "—"}</div>
              {proof.payer_cpf_middle && <div className="text-[11px] text-muted-foreground">CPF: ***.{proof.payer_cpf_middle.slice(0,3)}.{proof.payer_cpf_middle.slice(3)}.**</div>}
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground">Data Constante</div>
              <div className="font-medium">{proof.pix_date ? new Date(proof.pix_date).toLocaleDateString("pt-BR") : "—"}</div>
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground">Telefone (Origem WA)</div>
              <div className="font-medium">{proof.origin_phone || "—"}</div>
            </div>
          </div>

          {mode === "aguardando" && (
            <>
              <div>
                <Label className="text-xs">Vincular a uma cobrança pendente:</Label>
                {selectedOrder ? (
                  <div className="rounded-lg border border-primary ring-2 ring-primary/20 bg-primary/5 p-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-semibold">{selectedOrder.student_name || selectedOrder.parent_name || "—"}</div>
                      <MatchBadge level={scoreCandidate(proof, selectedOrder)} />
                    </div>
                    <div className="mt-1 text-sm">
                      <span className="text-muted-foreground">Valor Líquido: </span>
                      <span className="font-bold text-amber-600">{fmtMoney(selectedOrder.amount)}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">#{selectedOrder.id.slice(0, 8)}</span>
                      <span>{selectedOrder.item_name}</span>
                      {selectedOrder.parent_cpf && <span>CPF: {maskCpfMiddle(selectedOrder.parent_cpf) || selectedOrder.parent_cpf}</span>}
                      {selectedOrder.parent_name && <span>Resp.: {selectedOrder.parent_name}</span>}
                    </div>
                    <Button variant="ghost" size="sm" className="mt-2" onClick={() => setSearchOpen(true)}>
                      <Search className="h-4 w-4 mr-1" /> Trocar cobrança
                    </Button>
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed bg-muted/30 p-3 flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">
                      {awaiting.length === 0
                        ? "Nenhuma cobrança pendente disponível."
                        : "Nenhum match automático. Consulte as cobranças pendentes para vincular."}
                    </span>
                    <Button size="sm" variant="outline" disabled={awaiting.length === 0} onClick={() => setSearchOpen(true)}>
                      <Search className="h-4 w-4 mr-1" /> Consultar cobrança pendente para vincular
                    </Button>
                  </div>
                )}
                <div className="text-[11px] text-muted-foreground mt-1">
                  Após confirmar, o valor entra automaticamente no Caixa.
                </div>
              </div>

              <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Consultar cobrança pendente</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="grid gap-1">
                      <Label className="text-xs">Valor</Label>
                      <Input type="number" step="0.01" value={qAmount} onChange={(e) => setQAmount(e.target.value)} placeholder="Ex: 150,00" />
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-xs">Produto / item</Label>
                      <Input value={qItem} onChange={(e) => setQItem(e.target.value)} placeholder="Plano, uniforme…" />
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-xs">CPF / CNPJ</Label>
                      <Input value={qCpf} onChange={(e) => setQCpf(e.target.value)} placeholder="Somente números" />
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-xs">Responsável / aluno</Label>
                      <Input value={qName} onChange={(e) => setQName(e.target.value)} placeholder="Nome" />
                    </div>
                  </div>
                  <div className="space-y-2 max-h-[45vh] overflow-auto pr-1 mt-2">
                    {searchResults.length === 0 ? (
                      <div className="text-xs text-muted-foreground p-3">Nenhuma cobrança encontrada com esses filtros.</div>
                    ) : searchResults.map(({ o, level }) => {
                      const val = `${o.kind}:${o.id}`;
                      return (
                        <button
                          type="button"
                          key={val}
                          onClick={() => { setLinkVal(val); setSearchOpen(false); }}
                          className={`w-full text-left rounded-lg border p-3 transition ${
                            linkVal === val ? "border-primary ring-2 ring-primary/30 bg-primary/5" : "hover:bg-muted/40"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="font-semibold">{o.student_name || o.parent_name || "—"}</div>
                            <MatchBadge level={level} />
                          </div>
                          <div className="mt-1 text-sm">
                            <span className="text-muted-foreground">Valor Líquido: </span>
                            <span className="font-bold text-amber-600">{fmtMoney(o.amount)}</span>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">#{o.id.slice(0, 8)}</span>
                            <span>{o.item_name}</span>
                            {o.parent_cpf && <span>CPF: {maskCpfMiddle(o.parent_cpf) || o.parent_cpf}</span>}
                            {o.parent_name && <span>Resp.: {o.parent_name}</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </DialogContent>
              </Dialog>

              <div className="flex items-center justify-between flex-wrap gap-2">
                <Button variant="ghost" size="sm" onClick={remove} disabled={acting}>
                  <Trash2 className="h-4 w-4 mr-1" /> Excluir
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={reject} disabled={acting}>
                    <XCircle className="h-4 w-4 mr-1" /> Rejeitar
                  </Button>
                  <Button onClick={doConfirm} disabled={acting || !linkVal}>
                    {acting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                    Confirmar
                  </Button>
                </div>
              </div>
            </>
          )}

          {mode === "confirmado" && (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Button variant="ghost" size="sm" onClick={remove} disabled={acting}>
                <Trash2 className="h-4 w-4 mr-1" /> Excluir
              </Button>
              <div className="flex gap-2">
                {proof.bank_reconciled ? (
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={async () => {
                      try { await setReconFn({ data: { id: proof.id, reconciled: false } }); toast.success("Marcado como não conferido."); reload(); }
                      catch (e: any) { toast.error(e?.message); }
                    }}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Conferido
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      try { await setReconFn({ data: { id: proof.id, reconciled: true } }); toast.success("Conferido no extrato."); reload(); }
                      catch (e: any) { toast.error(e?.message); }
                    }}
                  >
                    <AlertCircle className="h-4 w-4 mr-1" /> Não conferido
                  </Button>
                )}
                <Button variant="outline" onClick={revert}>
                  <Undo2 className="h-4 w-4 mr-1" /> Reverter para não confirmado
                </Button>
              </div>
            </div>
          )}

          {mode === "rejeitado" && (
            <div className="flex items-center justify-end flex-wrap gap-2">
              <Button variant="outline" onClick={revert} disabled={acting}>
                <Undo2 className="h-4 w-4 mr-1" /> Reabrir
              </Button>
              <Button variant="destructive" onClick={remove} disabled={acting}>
                <Trash2 className="h-4 w-4 mr-1" /> Excluir Definitivamente
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ReconciliationPanel({ proofs, reload, filter, setFilter }: {
  proofs: Proof[]; reload: () => void;
  filter: "todos" | "conferidos" | "nao";
  setFilter: (v: "todos" | "conferidos" | "nao") => void;
}) {
  const csvFn = useServerFn(reconcileWithBankCsv);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    if (filter === "conferidos") return proofs.filter((p) => p.bank_reconciled);
    if (filter === "nao") return proofs.filter((p) => !p.bank_reconciled);
    return proofs;
  }, [proofs, filter]);


  const onCsv = async (file: File) => {
    setBusy(true);
    try {
      const txt = await file.text();
      const lines = txt.split(/\r?\n/).filter(Boolean);
      const rows: { date: string | null; amount: number; payer: string | null }[] = [];
      for (const ln of lines) {
        const parts = ln.split(/[;,\t]/).map((s) => s.trim());
        // try parse: date, amount, payer  OR  date, payer, amount
        let date: string | null = null;
        let amount: number = NaN;
        let payer: string | null = null;
        for (const part of parts) {
          const md = /^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/.exec(part);
          if (md && !date) { date = `${md[3]}-${md[2]}-${md[1]}`; continue; }
          const mi = /^(\d{4})-(\d{2})-(\d{2})$/.exec(part);
          if (mi && !date) { date = part; continue; }
          const num = Number(part.replace(/\./g, "").replace(",", "."));
          if (Number.isFinite(num) && Number.isNaN(amount)) { amount = num; continue; }
          if (!payer && /[a-zA-Z]/.test(part)) payer = part;
        }
        if (Number.isFinite(amount)) rows.push({ date, amount, payer });
      }
      if (!rows.length) { toast.error("Não foi possível ler linhas do CSV."); return; }
      const r: any = await csvFn({ data: { rows } });
      toast.success(`${r.matched} comprovante(s) marcados como conferidos.`);
      reload();
    } catch (e: any) { toast.error(e?.message ?? "Erro"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs flex gap-2">
          <Info className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <b>Conferência Automática:</b> Para realizar a conferência automática, importe seu <b>extrato bancário (arquivo CSV)</b>
            contendo as colunas <b>Valor, Data e Pagador</b>. O sistema fará a conferência desses dados com os
            comprovantes que ainda não foram conferidos no extrato.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1">
            <Button size="sm" variant={filter === "nao" ? "default" : "outline"} onClick={() => setFilter("nao")}>Não Conferidos</Button>
            <Button size="sm" variant={filter === "conferidos" ? "default" : "outline"} onClick={() => setFilter("conferidos")}>Conferidos</Button>
            <Button size="sm" variant={filter === "todos" ? "default" : "outline"} onClick={() => setFilter("todos")}>Todos</Button>
          </div>
          <div className="ml-auto">
            <input ref={fileRef} type="file" accept=".csv,text/csv" hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onCsv(f); }} />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Importar CSV
            </Button>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          Exibindo {filtered.length} de {proofs.length} confirmados.
        </div>
      </CardContent>
    </Card>
  );
}
